import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { later, run } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { TourId } from 'webapp/enums/tour';

import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectComponent from 'webapp/components/form/select';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';

import type { Source, SourceType } from 'webapp/models-custom';

import type { SourceListEntry } from 'webapp/services/model-manager/sources';

interface Args {
	sourceType: SourceType;
	sourceOptions: SourceListEntry[];
	sourcesOfType: SourceListEntry[];
	onSourceChange: (sourceKey: string) => void;
	source: Source | null;
}

export default class MetricLibrarySourceAboutComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('connect') declare connectService: Service.Connect;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('tour') declare tourService: Service.Tour;
	@service('popup') declare popupService: Service.Popup;

	get isConnected() {
		return !!this.args.source;
	}

	get canConnect() {
		return !this.args.sourceType.isHidden;
	}

	get avatarBig() {
		return this.args.sourceType.avatarBig;
	}

	get sourceName() {
		return this.args.sourceType.name;
	}

	get numberOfConectionsString() {
		const numberOfConnections = this.args.sourcesOfType.length;
		return `${numberOfConnections} Connection${numberOfConnections > 1 ? 's' : ''}`;
	}

	@action
	changeSource(sourceId: string) {
		this.args.onSourceChange(sourceId);
	}

	@action
	connectSource() {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		void this.connectService.connectSourceFromIdOrKey(this.args.sourceType.key, {
			origin: 'metrics',
		});
	}

	@action
	helpConnect() {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (this.currentUserService.model?.isVerified) {
			this.args.sourceType.requestConnect();
		} else {
			this.connectService.openEmailVerificationInfoPopup();
		}
	}

	@action
	startTour() {
		if (this.tourService.isEligibleForMetricsSourcesTour) {
			later(() => {
				if (!this.popupService.anyPopupsOpen) {
					const tour = this.tourService.initTour(TourId.METRICS_SOURCES);
					if (tour) {
						tour.start();
						$('.metric-library-source-about .source-type__select').on('click', () => {
							run(tour, tour.end);
						});
					}
				}
			}, 500);
		}
	}
	<template>
		<div class="metric-library-source-about" {{didInsert this.startTour}}>
			<div class="source-type">
				<img class="avatar avatar--big" src={{this.avatarBig}} width="185" alt />
				<div class="source-type__wrapper">
					<div class="source-type__info">
						<div class="source-type__name">
							{{this.sourceName}}
							{{#if this.isConnected}}
								<span class="source-type__badge">
									<CommonUiIconComponent @name="Connect" @size="sm" />
									{{this.numberOfConectionsString}}
								</span>
							{{/if}}
						</div>
						<div class="source-type__actions">
							{{#if this.canConnect}}
								{{#if this.isConnected}}
									<SourceConnectButtonComponent
										@sourceType={{@sourceType}}
										@onConnect={{this.connectSource}}
										@text="Connect another {{this.sourceName}}"
										@icon="Plus"
										@theme="green-outlined"
									/>
								{{else}}
									<SourceConnectButtonComponent
										@sourceType={{@sourceType}}
										@onConnect={{this.connectSource}}
										@size="big"
										@extraClass="connect-now next"
										@theme="green"
									/>
									{{#if @sourceType.showConnectHelp}}
										<a
											role="button"
											class="connect-help button size-big theme-blue-outlined"
											{{on "click" this.helpConnect}}
										>
											<CommonUiIconComponent @name="Mail" @size="sm" />Ask
											teammate to connect
										</a>
									{{/if}}
								{{/if}}
							{{/if}}
						</div>
					</div>
					<div class="source-type__selection">
						<img src="/images/placeholders/metrics2.png" alt="Search source" />
						<span class="source-type__selection-title">Select data source</span>
						<div class="source-type__select">
							<FormSelectComponent
								name="selectedSource"
								@selected={{@source.id}}
								@options={{@sourceOptions}}
								@onChange={{this.changeSource}}
								@isNumeric={{false}}
								@placeholder="Select a data source"
								@isRequired={{true}}
								@showFloatlabel={{false}}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</template>
}
